<template>
  <div v-if="showWidget" class="table-selector" tabindex="-1">
    <!-- QR or Kiosk Mode (Locked or Semi-locked) -->
    <div v-if="isFixedMode" class="table-selector__fixed-mode">
      <div class="table-selector__fixed-info">
        <span class="table-selector__fixed-label">
          {{ isKioskMode ? t('table.selector.kiosk_mode') : t('table.selector.qr_mode', 'Mesa asignada') }}:
        </span>
        <span class="table-selector__fixed-value">{{ currentTableCode }}</span>
      </div>
      
      <!-- Only QR allows changing table explicitly -->
      <button 
        v-if="!isKioskMode && !isSessionActive" 
        class="table-selector__change-btn"
        @click="enableManualSelection"
      >
        {{ t('table.selector.change_table', 'Cambiar mesa') }}
      </button>
    </div>

    <!-- Manual Mode -->
    <div v-else class="table-selector__manual-mode">
      <label for="table-select" class="table-selector__label">
        {{ t('table.selector.label') }}
      </label>
      
      <select
        id="table-select"
        v-model="selectedTableId"
        class="table-selector__input"
        :disabled="isSessionActive || isTableChanging || (availableTables.length === 0 && !isLoadingTables)"
        @change="onTableChange"
      >
        <option value="" disabled>
          {{ availableTables.length === 0 && !isLoadingTables ? t('table.selector.none_available', 'No hay mesas disponibles en este momento') : t('table.selector.placeholder') }}
        </option>
        <option
          v-for="table in allTables"
          :key="table.id"
          :value="table.id"
          :disabled="table.status === 'occupied'"
        >
          {{ table.table_number }}{{ table.status === 'occupied' ? ' (' + t('table.status.occupied', 'Ocupada') + ')' : '' }}
        </option>
      </select>

      <p
        v-if="availableTables.length === 0 && !isLoadingTables"
        class="table-selector__empty-hint"
        role="status"
      >
        {{ t('table.selector.none_available', 'No hay mesas disponibles en este momento') }}
      </p>
    </div>

    <p v-if="isSessionActive" class="table-selector__session-notice">
      {{ t('table.selector.session_active') }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useSessionStore } from '../../stores/session-store';
import { requestJSON } from '../../core/http';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const sessionStore = useSessionStore();

const emit = defineEmits<{
  (e: 'table-changed', tableId: string): void;
}>();

interface Table {
  id: string;
  table_number: string;
  status: string;
  is_active: boolean;
}

const allTables = ref<Table[]>([]);
const availableTables = computed(() => allTables.value.filter(t => t.status !== 'occupied'));
const selectedTableId = ref<string>('');
const forceManual = ref(false);
const isTableChanging = ref(false);
const isLoadingTables = ref(false);

const showWidget = computed(() => {
  // Always show widget unless there's an active session in old manual mode.
  // Actually, let's always show it in details page, but it's disabled if session is active.
  return true;
});

const isKioskMode = computed(() => {
  return sessionStore.tableContext?.source === 'kiosk' || sessionStore.tableContext?.locked_by_kiosk;
});

const isFixedMode = computed(() => {
  if (forceManual.value) return false;
  const source = sessionStore.tableContext?.source;
  return source === 'qr' || source === 'kiosk' || sessionStore.tableContext?.locked_by_kiosk;
});

const isSessionActive = computed((): boolean => {
  return !!(sessionStore.isAuthenticated && sessionStore.sessionId);
});

const currentTableCode = computed(() => {
  return sessionStore.tableContext?.table_code || sessionStore.tableContext?.table_id || 'N/A';
});

const loadTables = async () => {
  isLoadingTables.value = true;
  try {
    const response = await requestJSON<{ tables: Table[] }>('/api/tables');
    allTables.value = (response?.tables || []).filter(
      t => t.is_active !== false && !['indisposed', 'maintenance'].includes(t.status || ''),
    );
  } catch (error) {
    console.error('Failed to load tables:', error);
  } finally {
    isLoadingTables.value = false;
  }
};

const enableManualSelection = () => {
  forceManual.value = true;
  if (allTables.value.length === 0) {
    loadTables();
  }
};

const onTableChange = async () => {
  if (!selectedTableId.value) return;
  
  isTableChanging.value = true;
  try {
    const table = allTables.value.find(t => t.id === selectedTableId.value);
    await sessionStore.setTableContext(selectedTableId.value, 'manual', table?.table_number);
    // Only persist if we have a session context
    if (sessionStore.tableContext) {
      await sessionStore.persistRemoteTableContext(sessionStore.tableContext);
    }
    // Initialize session if not already active
    if (!sessionStore.sessionReady) {
      await sessionStore.initSession(selectedTableId.value);
    }
    emit('table-changed', selectedTableId.value);
  } catch (error) {
    console.error('Failed to set table context:', error);
  } finally {
    isTableChanging.value = false;
  }
};

const loadPersistedTable = () => {
  const persistedTableId = sessionStore.tableContext?.table_id;
  if (persistedTableId) {
    selectedTableId.value = persistedTableId;
  }
};

onMounted(async () => {
  loadPersistedTable();
  if (!isFixedMode.value && !isSessionActive.value) {
    await loadTables();
  }
});

watch(() => sessionStore.tableContext, (newContext) => {
  if (newContext?.table_id) {
    selectedTableId.value = newContext.table_id;
  }
}, { immediate: true });
</script>

<style scoped>
.table-selector {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  width: 100%;
  outline: none;
}

.table-selector__label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  margin: 0;
}

.table-selector__input {
  width: 100%;
  padding: 0.625rem 0.875rem;
  font-size: 0.875rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  color: #1f2937;
  background: white;
  transition: all 0.2s;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%236B7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E");
  background-position: right 0.5rem center;
  background-repeat: no-repeat;
  background-size: 1.5em 1.5em;
  padding-right: 2.5rem;
}

.table-selector__input:focus {
  outline: none;
  border-color: #e05a2a;
  box-shadow: 0 0 0 3px rgba(255, 107, 53, 0.1);
}

.table-selector__input:disabled {
  background: #f3f4f6;
  cursor: not-allowed;
  color: #9ca3af;
  opacity: 1;
}

.table-selector__fixed-mode {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 0.625rem 0.875rem;
  font-size: 0.875rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  background: #f3f4f6;
  color: #1f2937;
}

.table-selector__fixed-label {
  color: #6b7280;
  margin-right: 0.5rem;
}

.table-selector__fixed-value {
  font-weight: 600;
}

.table-selector__change-btn {
  background: none;
  border: none;
  color: #e05a2a;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  /* #832: WCAG 2.5.5 minimum touch target */
  min-width: 44px;
  min-height: 44px;
}

.table-selector__change-btn:hover {
  text-decoration: underline;
}

.table-selector__empty-hint {
  margin-top: 0.5rem;
  font-size: 0.8125rem;
  color: var(--enfa-color-neutral-600, #64748b);
  background-color: var(--enfa-color-neutral-100, #f1f5f9);
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  border: 1px dashed var(--enfa-color-neutral-300, #cbd5e1);
}

.table-selector__session-notice {
  margin-top: 0.25rem;
  font-size: 0.8125rem;
  color: #10b981;
}

@media (max-width: 768px) {
  .table-selector__input {
    font-size: 16px; /* Prevents zoom on iOS */
  }
}
</style>
